import { useCallback, useEffect, useState, type JSX } from 'react'
import { api, type User } from './api.js'
import { Chrome, type Section } from './components/Chrome.js'
import { AccountDetail } from './screens/AccountDetail.js'
import { ConfigConsole } from './screens/ConfigConsole.js'
import { NoteSearch } from './screens/NoteSearch.js'
import { ReviewQueue } from './screens/ReviewQueue.js'
import { SignIn } from './screens/SignIn.js'
import { TargetingList } from './screens/TargetingList.js'
import { T } from './tokens.js'

/**
 * Screen state for the whole SPA.
 *
 * **"Am I signed in" is a question only the server can answer.** The session
 * cookie is `HttpOnly`, so nothing here can look at it; the app asks
 * `GET /api/auth/me` once on mount and believes the 200 or the 401. That is
 * also why there is a `checking` phase at all: rendering the sign-in screen
 * while the answer is in flight would flash a form at a rep who is already
 * signed in, every single load.
 *
 * The signed-in half of this file is deliberately thin — the app bar and the
 * navigation are `components/Chrome.tsx` and the targeting list is Task 12.
 * What is here is what these tasks can honestly claim: a session that begins,
 * persists across a reload, and ends.
 */
type Phase =
  | { name: 'checking' }
  | { name: 'signedOut' }
  | { name: 'signedIn'; user: User }

export function App(): JSX.Element {
  const [phase, setPhase] = useState<Phase>({ name: 'checking' })

  useEffect(() => {
    // React 18's StrictMode runs this twice in development. `cancelled` keeps
    // the second run's answer from being overwritten by the first's; both are
    // GETs of the same route, so there is nothing else to undo.
    let cancelled = false
    void api.me()
      .then((user) => {
        if (cancelled) return
        setPhase(user === null ? { name: 'signedOut' } : { name: 'signedIn', user })
      })
      .catch(() => {
        // A network failure is not a signed-out session, but there is nothing
        // useful to render either way and the sign-in screen is the one screen
        // that works without a session. It will report the failure properly the
        // moment they try.
        if (!cancelled) setPhase({ name: 'signedOut' })
      })
    return () => { cancelled = true }
  }, [])

  const onSignedIn = useCallback((user: User) => { setPhase({ name: 'signedIn', user }) }, [])

  const signOut = useCallback(() => {
    // The local state changes whatever the server says. A logout that failed
    // still means this browser is done with the session, and leaving a rep on a
    // screen they asked to leave is the worse of the two outcomes.
    void api.logout().catch(() => undefined).finally(() => { setPhase({ name: 'signedOut' }) })
  }, [])

  if (phase.name === 'checking') return <Booting />
  if (phase.name === 'signedOut') return <SignIn onSignedIn={onSignedIn} />
  return <SignedIn user={phase.user} onSignOut={signOut} />
}

/**
 * The brand rule and nothing else, for the moment between mount and the
 * server's answer. Deliberately wordless: any copy here would be a claim about
 * a state the app does not know it is in yet.
 */
function Booting(): JSX.Element {
  return (
    <div style={{ minHeight: '100vh', fontFamily: T.fontFamily }}>
      <div data-testid="rainbow-rule" style={{ height: T.rainbowHeight, background: T.rainbow }} />
    </div>
  )
}

interface SignedInProps {
  user: User
  onSignOut: () => void
}

/**
 * Every screen this app has a URL for.
 *
 * **This used to be `{ accountId: string } | null`, and `null` used to mean the
 * list.** The comment here said "this app has exactly two screens" and gave
 * that as the reason there was no `list` variant: a two-member union where one
 * member is "everything else" invites a third that is neither. Plan 5's note
 * search is that third screen, so the union is tagged now and the list is a
 * member like the others — `null` as "the default screen" cannot survive a
 * second default-ish screen, and the tag is what makes the compiler point at
 * every place a new member has to be handled.
 */
export type Route =
  | { name: 'list' }
  | { name: 'account'; accountId: string }
  | { name: 'noteSearch' }
  | { name: 'config' }
  | { name: 'reviewQueue' }

const ACCOUNT_PATH = /^\/accounts\/([^/]+)\/?$/
const NOTES_PATH = /^\/notes\/?$/
const CONFIG_PATH = /^\/config\/?$/
const REVIEW_PATH = /^\/review\/?$/

/**
 * `/accounts/acct-desert-ridge` and `/notes` as routes, anything else as the
 * list.
 *
 * **Exported, and tested against the pathnames a browser actually produces.**
 * `decodeURIComponent` is the half that is easy to leave out and impossible to
 * notice: `TargetingList` writes the href with `encodeURIComponent`, so an
 * account id containing a character that survives encoding would round-trip
 * into a 404 on a route that looked right in the address bar. A malformed
 * escape (`%zz`) throws out of `decodeURIComponent` rather than returning the
 * input, so it is caught — a hand-typed URL must not take the whole SPA down
 * with an uncaught `URIError` before the first paint.
 *
 * `/notes`, `/config` and `/review` are matched before the account pattern only
 * because none of the four can overlap; the order carries no meaning and no
 * pattern is a prefix of another.
 *
 * `/config` is the configuration console (Plan 6 Task 7) and `/review` is the
 * analyst's characterization review queue (Task 8). Neither is gated **here**:
 * routing decides which screen a URL names, and who may see it is the server's
 * `requireRole` — `program_manager` on one, `analyst` on the other — plus each
 * screen's own reading of the 403 that comes back. A client-side route guard
 * would be a second answer to that question, and the weaker of the two.
 *
 * An unknown path is the list rather than a 404 screen. The server serves this
 * shell for every non-`/api` path (`http/static.ts`), so "unknown path" here
 * means a URL nothing in this app ever wrote, and dropping a rep on their book
 * is a better answer than a dead end.
 */
export function parseRoute(pathname: string): Route {
  if (NOTES_PATH.test(pathname)) return { name: 'noteSearch' }
  if (CONFIG_PATH.test(pathname)) return { name: 'config' }
  if (REVIEW_PATH.test(pathname)) return { name: 'reviewQueue' }
  const match = ACCOUNT_PATH.exec(pathname)
  if (match === null) return { name: 'list' }
  const raw = match[1]
  if (raw === undefined || raw === '') return { name: 'list' }
  try {
    return { name: 'account', accountId: decodeURIComponent(raw) }
  } catch {
    return { name: 'list' }
  }
}

/**
 * Which app-bar tab is the one the rep is on.
 *
 * A `switch` rather than the nested ternary this was, and the return type is
 * annotated for the same reason the screen `switch` below is: a fifth member of
 * `Route` that nobody mapped is a compile error rather than a screen rendered
 * under somebody else's tab. That is not hypothetical — the ternary this
 * replaces answered `'targeting'` for every route it had not heard of, so the
 * configuration console would have underlined `Targeting` and said nothing.
 */
function sectionFor(route: Route): Section {
  switch (route.name) {
    case 'noteSearch': return 'notes'
    case 'config': return 'program-config'
    case 'reviewQueue': return 'review-queue'
    case 'list':
    case 'account': return 'targeting'
  }
}

function pathFor(route: Route): string {
  if (route.name === 'noteSearch') return '/notes'
  if (route.name === 'config') return '/config'
  if (route.name === 'reviewQueue') return '/review'
  if (route.name === 'account') return `/accounts/${encodeURIComponent(route.accountId)}`
  return '/'
}

/**
 * The signed-in shell: `Chrome` draws the rainbow rule, the app bar and the
 * navigation, and the body is the targeting list or one account's drill-down.
 *
 * **The address bar follows, and it is what the screen is read from.** Task 12
 * made every account name a real `<a href="/accounts/:id">` and intercepted the
 * ordinary click without moving the URL, which left a rep unable to send a
 * colleague the account they were looking at. The route is now derived from
 * `location.pathname` at mount, so a deep link renders this screen *directly*
 * rather than redirecting through the list — `http/static.ts` already serves
 * the shell for that path, so the two halves meet.
 *
 * There is still no router library. **Five** screens and one parameter do not
 * need one either: `history.pushState` plus a `popstate` listener is the whole
 * of what a router would do here — with the browser's own Back button working,
 * which is the thing a rep will actually reach for. What the third screen did
 * change is the shape of `Route`, which is a tagged union now rather than
 * "an account, or `null` for everything else"; the fourth and fifth cost one
 * line each in four places because of it, and every one of those places is a
 * compile error until it is written.
 *
 * **The note search is reached by a plain link, not by `go`.** `Chrome`'s
 * section tabs are real `<a href>`s and always have been, so moving between
 * sections is an ordinary navigation and the SPA boots again on the other side.
 * `go` exists for the list-to-account move, which happens over and over inside
 * one session and must keep the rail's filters alive.
 */
function SignedIn({ user, onSignOut }: SignedInProps): JSX.Element {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname))
  /**
   * The name of the what-if the drill-down currently has on screen, or `null`.
   *
   * It lives here because R-18 puts the scenario treatment in the **app
   * chrome** — a 6px red bar over the brand rule and the name beside the
   * wordmark — and the chrome is above the screen that owns the scenario. This
   * is the only piece of a drill-down's state that travels up, and it travels
   * as a name rather than as the payload: nothing up here draws a figure.
   */
  const [scenarioName, setScenarioName] = useState<string | null>(null)

  useEffect(() => {
    // Back and Forward. Without this the URL changes and the screen does not,
    // which is worse than never having moved the URL at all: the address bar
    // then names a page the rep is not looking at.
    const onPop = (): void => { setRoute(parseRoute(window.location.pathname)) }
    window.addEventListener('popstate', onPop)
    return () => { window.removeEventListener('popstate', onPop) }
  }, [])

  const go = useCallback((next: Route) => {
    const path = pathFor(next)
    // `pushState` and not `replaceState`: opening an account is a place a rep
    // came from somewhere to get to, and Back must return them to their book
    // with the filters they had set — which are `TargetingList`'s own state and
    // survive because the list is only unmounted, not reloaded.
    if (window.location.pathname !== path) window.history.pushState(null, '', path)
    setRoute(next)
  }, [])

  const onOpen = useCallback((accountId: string) => { go({ name: 'account', accountId }) }, [go])
  const onBack = useCallback(() => { go({ name: 'list' }) }, [go])

  /**
   * A `switch` over the tag rather than a chain of ternaries: the return type
   * is annotated, so a fourth member of `Route` that nobody handled is a
   * compile error here rather than a screen that silently falls through to the
   * list.
   */
  let body: JSX.Element
  switch (route.name) {
    case 'list':
      body = <TargetingList user={user} onOpen={onOpen} />
      break
    case 'noteSearch':
      // `user` is passed rather than omitted: it is what lets the search offer
      // "only my notes", the only author filter this app can honestly draw
      // (see `NoteSearch`).
      body = <NoteSearch user={user} />
      break
    case 'config':
      // No `user`. The console needs none: every question it could ask the prop
      // — may I edit this, whose draft is this — the server answers on the
      // routes themselves, and a screen that decided from a prop would be
      // deciding from something a browser can edit.
      body = <ConfigConsole />
      break
    case 'reviewQueue':
      // No `user` either, and for the same reason as the console: the queue,
      // the impact preview and the decision are all `requireRole('analyst')`
      // on the server, and a screen that decided from a prop would be deciding
      // from something a browser can edit. The decision's reviewer comes from
      // the session, never from this process.
      body = <ReviewQueue />
      break
    case 'account':
      body = (
        <AccountDetail
          accountId={route.accountId}
          onBack={onBack}
          onScenario={setScenarioName}
        />
      )
      break
  }

  return (
    <Chrome
      user={user}
      active={sectionFor(route)}
      onSignOut={onSignOut}
      scenario={scenarioName}
    >
      {body}
    </Chrome>
  )
}
